import { Container, Sprite, Ticker, type Texture } from 'pixi.js';

// One loaded particle: texture + the px size to render it at (longest side).
// Built by the asset loader from each ParticleSpec in the character bundle.
export type LoadedParticle = { texture: Texture; size: number };
export type DamageParticleSet = readonly LoadedParticle[];

// Physics tuned for "snappy impact debris" feel. We use REAL wall-time deltas
// (not ticker.deltaMS) so particles don't slow down with BATTLE_PACE_SCALE -
// hit fx should pop even when the rest of the battle is in slow-mo.
const GRAVITY = 1500;            // px/s^2
const BOUNCE_DAMP_Y = 0.45;      // vy multiplier on bounce
const BOUNCE_DAMP_X = 0.7;       // vx friction on ground contact
const BOUNCE_DAMP_ROT = 0.7;
const REST_VY_THRESHOLD = 80;    // |vy| below this on impact -> stop instead of bounce
const FADE_DURATION_MS = 250;

const VX_RANGE: [number, number] = [-180, 180];
const VY_RANGE: [number, number] = [-450, -250]; // negative = up
const VROT_RANGE: [number, number] = [-8, 8];    // rad/s
const SPAWN_OFFSET_X: [number, number] = [-20, 20];
const SPAWN_OFFSET_Y: [number, number] = [-20, 20];
const DIRECTION_BIAS_VX = 80;    // bias added to vx when hit comes from a side

class GearParticle extends Container {
  private vx: number;
  private vy: number;
  private vrot: number;
  private bouncesLeft = 1;
  private fading = false;
  private fadeRemaining = FADE_DURATION_MS;
  private lastTime = performance.now();
  private readonly groundY: number;
  private readonly tickerCb: () => void;
  private readonly sprite: Sprite;

  constructor(opts: {
    texture: Texture;
    size: number;
    x: number;
    y: number;
    groundY: number;
    vx: number;
    vy: number;
    vrot: number;
  }) {
    super();
    this.sprite = new Sprite(opts.texture);
    this.sprite.anchor.set(0.5);
    const longestSide = Math.max(opts.texture.width, opts.texture.height);
    const scale = longestSide > 0 ? opts.size / longestSide : 1;
    this.sprite.scale.set(scale);
    this.addChild(this.sprite);
    this.position.set(opts.x, opts.y);
    this.vx = opts.vx;
    this.vy = opts.vy;
    this.vrot = opts.vrot;
    this.groundY = opts.groundY;
    this.tickerCb = () => this.update();
    Ticker.shared.add(this.tickerCb);
  }

  private update(): void {
    if (this.destroyed) return;
    const now = performance.now();
    const dtMs = now - this.lastTime;
    this.lastTime = now;
    const dt = dtMs / 1000;

    this.vy += GRAVITY * dt;
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.sprite.rotation += this.vrot * dt;

    if (this.y >= this.groundY) {
      // Snap to ground regardless - prevents drifting below baseline visually.
      this.y = this.groundY;
      if (this.bouncesLeft > 0 && this.vy > REST_VY_THRESHOLD) {
        this.vy = -this.vy * BOUNCE_DAMP_Y;
        this.vx *= BOUNCE_DAMP_X;
        this.vrot *= BOUNCE_DAMP_ROT;
        this.bouncesLeft--;
      } else {
        this.vy = 0;
        this.vx = 0;
        this.fading = true;
      }
    }

    if (this.fading) {
      this.fadeRemaining -= dtMs;
      this.alpha = Math.max(0, this.fadeRemaining / FADE_DURATION_MS);
      if (this.fadeRemaining <= 0) this.dispose();
    }
  }

  private dispose(): void {
    this.destroy({ children: true });
  }

  // Ticker removal lives in destroy() so scene teardown (scene.destroy with
  // { children: true }) also stops the callback, not just self-dispose on fade.
  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    Ticker.shared.remove(this.tickerCb);
    super.destroy(options);
  }
}

export function spawnGearBurst(opts: {
  layer: Container;
  particles: DamageParticleSet;
  x: number;
  y: number;
  groundY: number;
  count?: number;
  // -1 = particles biased to fly left, +1 = biased right, 0 = symmetric.
  // Use sign of (target.x - attacker.x) to make debris fly away from the punch.
  directionBias?: -1 | 0 | 1;
}): void {
  if (opts.particles.length === 0) return;
  const count = opts.count ?? 5;
  const bias = opts.directionBias ?? 0;
  for (let i = 0; i < count; i++) {
    const idx = Math.floor(Math.random() * opts.particles.length);
    const spec = opts.particles[idx];
    if (!spec) continue;
    const vx = randRange(VX_RANGE[0], VX_RANGE[1]) + bias * DIRECTION_BIAS_VX;
    const vy = randRange(VY_RANGE[0], VY_RANGE[1]);
    const vrot = randRange(VROT_RANGE[0], VROT_RANGE[1]);
    const sx = opts.x + randRange(SPAWN_OFFSET_X[0], SPAWN_OFFSET_X[1]);
    const sy = opts.y + randRange(SPAWN_OFFSET_Y[0], SPAWN_OFFSET_Y[1]);
    const p = new GearParticle({
      texture: spec.texture,
      size: spec.size,
      x: sx,
      y: sy,
      groundY: opts.groundY,
      vx,
      vy,
      vrot,
    });
    opts.layer.addChild(p);
  }
}

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}
